// tabs/MarketTab.jsx
// Market analysis tab component showing market size, growth, and geographic distribution
// Extracted from CompetitiveAnalysisView.jsx for better maintainability

import React from 'react';
import {
    LineChart, Line, PieChart, Pie, BarChart, Bar,
    XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell
} from 'recharts';
import HelpIcon from '../shared/HelpIcon';
import MetricCard from '../shared/MetricCard';
import { COLORS, formatRevenue } from '../shared/constants';

/**
 * Market analysis tab component
 * @param {Object} props - Component props
 * @param {Object} props.analysis - Complete competitive analysis data (dual-source)
 * @returns {JSX.Element} Market tab component
 */
const MarketTab = ({ analysis }) => {
    // Extract from gamalyticsNetwork (new structure) or fallback to old structure
    const gamalyticsData = analysis?.gamalyticsNetwork || analysis || {};
    
    // Safe extraction with defaults
    const market = gamalyticsData?.market || {};
    const pricing = gamalyticsData?.pricing || {};
    const geography = gamalyticsData?.geography || {};

    const marketOverview = market?.overview || {};
    const marketSize = marketOverview?.size || { totalRevenue: 0, averageRevenue: 0 };
    const marketTrends = marketOverview?.trends || [];
    const pricingMarket = pricing?.market || {};
    const priceDistribution = pricingMarket?.distribution || {};
    const pricePoints = pricingMarket?.pricePoints || [];
    const geographyMarkets = geography?.markets || {};
    const geographyShares = geographyMarkets?.shares || [];
    const primaryMarkets = geographyMarkets?.primary || [];

    return (
        <div className="p-6 space-y-6">
            {/* Explanation banner */}
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-6">
                <div className="text-blue-700 font-medium mb-1">Understanding Market Analysis</div>
                <p className="text-blue-600 text-sm">
                    This section analyzes the market size, growth trends, and geographic distribution of competitor games.
                    Use these insights to identify untapped markets and revenue opportunities.
                </p>
            </div>

            {/* Market Size & Growth */}
            <div>
                <h3 className="text-lg font-medium mb-4 flex items-center">
                    Market Size & Growth
                    <HelpIcon
                        title="Market Size & Growth"
                        description="The total and average revenue in this market segment, showing the potential revenue opportunity."
                    />
                </h3>
                <div className="grid grid-cols-3 gap-4 mb-6">
                    <MetricCard
                        label="Total Market Size"
                        value={formatRevenue(marketSize.totalRevenue)}
                        trend={marketSize.growth}
                        tooltip="The estimated total revenue of this market segment"
                    />
                    <MetricCard
                        label="Average Revenue"
                        value={formatRevenue(marketSize.averageRevenue)}
                        tooltip="The average revenue per game in this segment"
                    />
                    <MetricCard
                        label="Market Stage"
                        value={market?.overview?.maturity?.stage || 'Growing'}
                        tooltip="The current life cycle stage of this market"
                    />
                </div>
                
                {/* Market trends chart */}
                {marketTrends.length > 0 && (
                    <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={marketTrends}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="period" />
                                <YAxis />
                                <Tooltip />
                                <Line type="monotone" dataKey="value" stroke="#3B82F6" />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </div>

            {/* Pricing Analysis */}
            <div>
                <h3 className="text-lg font-medium mb-4 flex items-center">
                    Pricing Landscape
                    <HelpIcon
                        title="Pricing Landscape"
                        description="Analysis of competitor pricing strategies and market price distribution to help you position your game competitively."
                    />
                </h3>
                
                {pricePoints.length > 0 && (
                    <div className="grid grid-cols-2 gap-6">
                        <div className="h-64">
                            <h4 className="text-sm font-medium mb-2">Price Distribution</h4>
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={pricePoints}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="range" />
                                    <YAxis />
                                    <Tooltip />
                                    <Bar dataKey="count" fill="#3B82F6" />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                        
                        <div>
                            <h4 className="text-sm font-medium mb-2">Price Insights</h4>
                            <div className="space-y-2">
                                <MetricCard
                                    label="Average Price"
                                    value={formatRevenue(pricingMarket?.averagePrice || 0)}
                                    tooltip="Average price across all competitor games"
                                />
                                <MetricCard
                                    label="Most Common Range"
                                    value={pricingMarket?.popularRange || 'N/A'}
                                    tooltip="The price range with the most games"
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Regional Distribution */}
            <div>
                <h3 className="text-lg font-medium mb-4 flex items-center">
                    Geographic Distribution
                    <HelpIcon
                        title="Geographic Distribution"
                        description="Shows where competitor games are strong and where opportunities exist. Markets with low percentages may be untapped opportunities."
                    />
                </h3>
                
                {geographyShares.length > 0 && (
                    <div className="grid grid-cols-2 gap-6">
                        <div className="h-64">
                            <h4 className="text-sm font-medium mb-2">Market Share by Region</h4>
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={geographyShares}
                                        cx="50%"
                                        cy="50%"
                                        labelLine={false}
                                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                        outerRadius={80}
                                        fill="#8884d8"
                                        dataKey="value"
                                    >
                                        {geographyShares.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                        
                        <div>
                            <h4 className="text-sm font-medium mb-2">Primary Markets</h4>
                            <div className="space-y-2">
                                {primaryMarkets.slice(0, 5).map((market, index) => (
                                    <div key={index} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                                        <span className="font-medium">{market.region || 'Unknown'}</span>
                                        <span className="text-sm text-gray-600">
                                            {(market.share || 0).toFixed(1)}%
                                        </span>
                                    </div>
                                ))}
                                {primaryMarkets.length === 0 && (
                                    <div className="text-sm text-gray-600 p-2">
                                        No geographic data available
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MarketTab;
